import {computed, makeObservable, observable} from 'mobx';

import autobind from 'common/decorators/autobind.js';
import http from 'http.js';
import Store from 'core/stores/Store.js';

// Below this, we ask the user for more rather than searching.
export const MIN_QUERY_LENGTH = 3;
export const RESULT_LIMIT = 10;
// Trailing, not leading. The Knockout version used
// `.extend({rateLimit: 200, method: 'notifyWhenChangesStop'})`, which fires
// once the user stops typing; a leading debounce would fire on the first
// keystroke and search for a single letter.
export const DEBOUNCE_MS = 200;

/**
 * Search-by-name for the health fund widget.
 *
 * Ported from directory/widget/name_search_manager.js. The behaviours worth
 * keeping, and easy to lose:
 *
 *   - a trailing debounce, so we search when typing stops
 *   - an in-flight request is aborted when a newer one starts, so a slow
 *     answer can never overwrite a fresher one
 *   - specialty ids are resolved to names through a map fetched once
 *   - the matched part of a name is marked up for highlighting, without
 *     putting user input anywhere near dangerouslySetInnerHTML
 */
export default class NameSearchStore extends Store {
    constructor({filter, healthFundId, source, specialtyId} = {}) {
        super();

        makeObservable(this, {
            highlightedIndex: observable,
            loading: observable,
            query: observable,
            results: observable.shallow,
            specialtyNames: observable.shallow,
            totalCount: observable,

            hasQuery: computed,
            showTooShort: computed,
            showNoResults: computed,
        });

        this.filter = filter || null;
        this.healthFundId = healthFundId || null;
        this.source = source || null;
        this.specialtyId = specialtyId || null;

        this.abortController = null;
        this.debounceTimer = null;
    }

    highlightedIndex = -1;
    loading = false;
    query = '';
    results = [];
    specialtyNames = {};
    totalCount = -1;

    get hasQuery() {
        return this.query.length > 0;
    }

    get showTooShort() {
        return this.hasQuery && this.query.length < MIN_QUERY_LENGTH;
    }

    get showNoResults() {
        return (
            this.query.length >= MIN_QUERY_LENGTH &&
            !this.loading &&
            this.results.length === 0
        );
    }

    /**
     * Specialty ids come back on each result; the names come from one call
     * made when the widget mounts. A failure here costs the specialty line on
     * a result, not the search, so it must not reject.
     */
    @autobind
    async loadSpecialtyNames() {
        try {
            const data = await http.get({
                url: '/api/professionals/v1/specialties/',
            });
            const specialtyNames = {};
            for (const specialty of data) {
                specialtyNames[specialty.id] = specialty.professional_name;
            }
            this.updateStore({specialtyNames});
        } catch (error) {
            // leave the map empty; results still render without specialties
        }
    }

    @autobind
    setQuery(query) {
        this.updateStore({query, highlightedIndex: -1});

        clearTimeout(this.debounceTimer);

        if (query.length < MIN_QUERY_LENGTH) {
            this.abort();
            this.updateStore({results: [], totalCount: -1, loading: false});
            return;
        }

        this.debounceTimer = setTimeout(this.search, DEBOUNCE_MS);
    }

    @autobind
    abort() {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }

    @autobind
    async search() {
        // A newer search always wins: cancel whatever is still in flight
        // before starting, so an earlier, slower answer cannot land last.
        this.abort();
        this.abortController = new AbortController();

        const data = {
            limit: RESULT_LIMIT,
            name: this.query,
        };
        if (this.healthFundId) {
            data.health_fund = this.healthFundId;
        }
        if (this.filter) {
            data.filter = this.filter;
        }
        if (this.specialtyId) {
            data.specialty = this.specialtyId;
        }

        this.updateStore({loading: true, results: [], totalCount: -1});

        try {
            const response = await http.get({
                data,
                signal: this.abortController.signal,
                url: '/api/professionals/v1/search/',
            });
            this.updateStore({
                loading: false,
                results: response.results.map((result) =>
                    this.toResult(result),
                ),
                totalCount: response.count,
            });
        } catch (error) {
            if (this.wasAborted(error)) {
                return;
            }
            this.updateStore({loading: false});
        }
    }

    wasAborted(error) {
        return (
            error &&
            (error.code === 'ERR_CANCELED' || error.name === 'CanceledError')
        );
    }

    toResult(data) {
        return {
            avatar: data.avatar,
            displayName: [data.title, data.first_name, data.last_name]
                .filter(Boolean)
                .join(' '),
            id: data.id,
            locality: data.location,
            specialties: (data.specialties || [])
                .map((id) => this.specialtyNames[id])
                .filter(Boolean),
            url: this.source
                ? `${data.profile_url}?source=${this.source}`
                : data.profile_url,
        };
    }

    @autobind
    moveHighlight(delta) {
        if (!this.results.length) {
            return;
        }
        const next = this.highlightedIndex + delta;
        this.updateStore({
            highlightedIndex: Math.min(
                Math.max(next, 0),
                this.results.length - 1,
            ),
        });
    }

    @autobind
    clear() {
        this.abort();
        clearTimeout(this.debounceTimer);
        this.updateStore({
            highlightedIndex: -1,
            results: [],
            totalCount: -1,
        });
    }

    get highlightedResult() {
        return this.results[this.highlightedIndex] || null;
    }
}
